package com.futurions.portfolio.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backdropFilter
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgba

@Composable
fun SkillsSection() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 60.px, bottom = 60.px)
            .id("skills"),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .maxWidth(1100.px)
                .padding(32.px)
                .backgroundColor(rgba(255, 255, 255, 0.04))
                .backdropFilter(blur(12.px))
                .borderRadius(24.px)
                .boxShadow(
                    offsetX = 0.px,
                    offsetY = 2.px,
                    blurRadius = 24.px,
                    spreadRadius = 8.px,
                    color = rgba(0, 0, 0, 0.3)
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            SpanText(
                text = "💡 Skills & Technologies",
                modifier = Modifier
                    .fontSize(30.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Color.white)
                    .padding(bottom = 30.px)
            )

            // ✅ Grid of cards
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .justifyContent(JustifyContent.Center)
                    .flexWrap(FlexWrap.Wrap)
                    .gap(24.px)
            ) {

                SkillCard("Kotlin", "/tools/Kotlin.png", "My main language for building modern apps.")
                SkillCard("Java", "/tools/java.png", "I build traditional Android apps using Java + XML.")
                SkillCard("Kotlin Multiplatform", "/tools/kmp.png", "I build cross-platform shared logic with KMP.")
                SkillCard("Jetpack Compose", "/tools/compose1.png", "My toolkit for beautiful, modern UI and animations.")
                SkillCard("Firebase", "/tools/Firebase.svg", "Auth, Firestore & realtime features for apps.")
                SkillCard("Room DB", "https://www.kodeco.com/assets/murakami/category-icons/category-saving-data-persistence-android-9dc9eed34348d1d06a4365379fba3a54aa7aabd05d828dbb4de97349126d9718.svg", "Local offline storage with clean structure.")
                SkillCard("XML", "/tools/kobweb_xml.png", "Used for classic Android UI when needed.")

                SkillCard("Unreal Engine", "https://www.citypng.com/public/uploads/preview/hd-unreal-engine-white-logo-icon-png-7017516949697958pnkct2kiz.png", "I create gameplay using Blueprints.")
                SkillCard("Godot", "/tools/gd_script.png", "I develop fast 2D gameplay in GDScript.")

                SkillCard("Figma", "/tools/figma_1.png", "Where I design UI before development.")
                SkillCard("GitHub", "/tools/GitHub.svg", "Version control & open-source work.")
                SkillCard("Android Studio", "/tools/Android.png", "My main IDE for app development.")
            }
        }
    }
}


@Composable
fun SkillCard(title: String, icon: String, description: String) {
    var hovered by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .width(240.px)
            .padding(16.px)
            .borderRadius(20.px)
            .backgroundColor(rgba(255, 255, 255, 0.06))
            .backdropFilter(blur(10.px))
            .boxShadow(
                offsetX = 0.px,
                offsetY = 2.px,
                blurRadius = 20.px,
                color = rgba(0, 0, 0, 0.25)
            )
            .styleModifier {
                property("transition", "all 0.25s ease")
                if (hovered) {
                    property("transform", "translateY(-8px) scale(1.03)")
                    property("box-shadow", "0 0 25px rgba(0,255,255,0.4)")
                    property("border", "1px solid rgba(0,255,255,0.3)")
                }
            }
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        Image(
            src = icon,
            description = title,
            modifier = Modifier
                .size(80.px)
                .padding(bottom = 12.px)
        )

        SpanText(
            text = title,
            modifier = Modifier
                .fontSize(20.px)
                .fontWeight(FontWeight.Medium)
                .color(Color.white)
                .padding(bottom = 8.px)
        )

        SpanText(
            text = description,
            modifier = Modifier
                .fontSize(14.px)
                .lineHeight(22.px)
                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(210,210,210))
                .textAlign(TextAlign.Center)
        )
    }
}

